import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer } from '@react-google-maps/api';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Drawer,
    IconButton,
    Divider,
    Chip,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Badge,
    Collapse,
    ListItemButton,
    TextField,
    InputAdornment,
    Tooltip,
    Button,
    Switch,
    FormControlLabel
} from '@mui/material';
import { formatTime, formatDateTime } from '../utils/dateHelpers';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    DirectionsCar as CarIcon,
    Route as RouteIcon,
    Schedule as ScheduleIcon,
    AttachMoney as MoneyIcon,
    People as PeopleIcon,
    LocationOn as LocationIcon,
    AccessTime as TimeIcon,
    Notes as NotesIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Circle as CircleIcon,
    EventNote as EventNoteIcon,
    SignalWifiOff as OfflineIcon,
    SignalWifi4Bar as OnlineIcon,
    PlayArrow as PickupTimeIcon,
    Timer as DurationIcon,
    EventAvailable as ScheduledIcon,
    Place as PlaceIcon,
    FlagOutlined as DropoffIcon,
    MultipleStop as StopsIcon,
    FormatListBulleted as ListIcon,
    Search as SearchIcon,
    MyLocation as MyLocationIcon,
    Message as MessageIcon,
    Map as MapIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useGoogleMaps } from './GoogleMapsProvider';
import { getMapCenter, getMapZoom } from '../config/googleMapsConfig';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { safeInvoke } from '../utils/signalRHelper';
import { driversAPI, dashboardAPI } from '../services/apiService';
import { MAP_MARKER_COLORS, MAP_RIDE_STATUS_COLORS, MAP_RIDE_STATUS_LABELS, getSettingsMapMarkerColors, getSettingsMapRideStatusColors } from '../config/appConstants';

// Map container style
const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 200px)',
    minHeight: '500px'
};

// Map options - gestureHandling: 'greedy' allows user to pan/zoom without ctrl key
const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: true,
    gestureHandling: 'greedy',
};

// Driver marker icon (car icon) - deep red for driving online, gray for driving offline
const getDrivingDriverIcon = (isOnline, c = MAP_MARKER_COLORS) => ({
    path: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
    fillColor: isOnline ? c.driver.driving.fill : c.driver.drivingOffline.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.driver.driving.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 12 }
});

// Available driver marker - green
const getAvailableDriverIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
    fillColor: c.driver.available.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.driver.available.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 12 }
});

// Driver with upcoming ride marker - orange/amber (pulsing effect via larger scale)
const getUpcomingRideDriverIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
    fillColor: c.driver.upcomingRide.fill,
    fillOpacity: 1,
    strokeWeight: 3,
    strokeColor: c.driver.upcomingRide.stroke,
    scale: 1.8,
    anchor: { x: 12, y: 12 }
});

// Highlighted driver marker - cyan/teal with glow effect for selected ride's driver
const getHighlightedDriverIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
    fillColor: c.driver.highlighted.fill,
    fillOpacity: 1,
    strokeWeight: 4,
    strokeColor: c.driver.highlighted.stroke,
    scale: 2.0,
    anchor: { x: 12, y: 12 }
});

// Ride marker icons - location pin style
// Red: Past ride with no pickup (missed/late)
const getMissedRideIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: c.ride.missed.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.ride.missed.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 22 }
});

// Yellow: Ride in progress (picked up, not dropped off)
const getInProgressRideIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: c.ride.inProgress.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.ride.inProgress.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 22 }
});

// Green: Future ride
const getFutureRideIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: c.ride.future.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.ride.future.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 22 }
});

// Gray dotted: Completed ride
const getCompletedRideIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: c.ride.completed.fill,
    fillOpacity: 0.6,
    strokeWeight: 2,
    strokeColor: c.ride.completed.stroke,
    strokeOpacity: 0.8,
    scale: 1.3,
    anchor: { x: 12, y: 22 }
});

// Blue: At Pickup (driver arrived at pickup location)
const getAtPickupRideIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: c.ride.atPickup.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.ride.atPickup.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 22 }
});

// Purple: Dropped Off (awaiting completion/payment)
const getDroppedOffRideIcon = (c = MAP_MARKER_COLORS) => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: c.ride.droppedOff.fill,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: c.ride.droppedOff.stroke,
    scale: 1.5,
    anchor: { x: 12, y: 22 }
});

// Helper to determine ride status and get appropriate icon
const getRideStatus = (ride) => {
    const now = new Date();
    const scheduledFor = new Date(ride.scheduledFor + (ride.scheduledFor.endsWith('Z') ? '' : 'Z'));

    // Completed: ride is fully completed
    if (ride.completed) {
        return 'completed';
    }

    // Dropped Off: has dropOffTime but not yet completed (awaiting payment)
    if (ride.dropOffTime && !ride.completed) {
        return 'droppedOff';
    }

    // In Progress: has pickupTime but no dropOffTime
    if (ride.pickupTime && !ride.dropOffTime) {
        return 'inProgress';
    }

    // At Pickup: driver arrived at pickup location but hasn't picked up yet
    if (ride.arrivedAtPickupTime && !ride.pickupTime) {
        return 'atPickup';
    }

    // Missed/Late: scheduled time has passed and no pickup
    if (scheduledFor < now && !ride.pickupTime) {
        return 'missed';
    }

    // Future: scheduled time is in the future
    return 'future';
};

const getRideIcon = (status, c = MAP_MARKER_COLORS) => {
    switch (status) {
        case 'missed':
            return getMissedRideIcon(c);
        case 'atPickup':
            return getAtPickupRideIcon(c);
        case 'inProgress':
            return getInProgressRideIcon(c);
        case 'droppedOff':
            return getDroppedOffRideIcon(c);
        case 'completed':
            return getCompletedRideIcon(c);
        case 'future':
        default:
            return getFutureRideIcon(c);
    }
};

const getRideStatusColor = (status, colors = MAP_RIDE_STATUS_COLORS) => colors[status] || colors.future;

const getRideStatusLabel = (status) => MAP_RIDE_STATUS_LABELS[status] || MAP_RIDE_STATUS_LABELS.future;

/**
 * Format "last seen" time as relative string with warning for stale locations
 * @param {Date|string} lastUpdate - The last update timestamp
 * @returns {{ text: string, isStale: boolean, isVeryStale: boolean }}
 */
const formatLastSeen = (lastUpdate) => {
    if (!lastUpdate) return { text: 'Unknown', isStale: true, isVeryStale: true };

    const updateTime = new Date(lastUpdate);
    const now = new Date();
    const diffMs = now - updateTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 30) {
        return { text: 'Just now', isStale: false, isVeryStale: false };
    } else if (diffSecs < 60) {
        return { text: `${diffSecs}s ago`, isStale: false, isVeryStale: false };
    } else if (diffMins < 3) {
        return { text: `${diffMins} min${diffMins > 1 ? 's' : ''} ago`, isStale: false, isVeryStale: false };
    } else if (diffMins < 10) {
        return { text: `${diffMins} mins ago`, isStale: true, isVeryStale: false };
    } else if (diffMins < 60) {
        return { text: `${diffMins} mins ago`, isStale: true, isVeryStale: true };
    } else {
        const hours = Math.floor(diffMins / 60);
        return { text: `${hours}h ago`, isStale: true, isVeryStale: true };
    }
};

/**
 * DriverTracking Component
 * 
 * Displays a Google Map showing real-time driver locations.
 * - Shows drivers with active rides (even if offline) in blue/gray
 * - Shows online/available drivers (no active ride) in green
 * - Click on driver to open side panel with details and upcoming calls
 * - Map only moves when user interacts with it (not auto-centering)
 */
const DriverTracking = ({ isAdmin = false, openMessagingModal }) => {
    const { isLoaded, loadError } = useGoogleMaps();
    const { signalRConnection, connectSignalR } = useAuth();
    const [map, setMap] = useState(null);
    const [drivingDrivers, setDrivingDrivers] = useState([]);
    const [onlineDrivers, setOnlineDrivers] = useState([]);
    const [driverLocations, setDriverLocations] = useState({});
    const [hoveredDriver, setHoveredDriver] = useState(null);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelDriver, setSidePanelDriver] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [upcomingCallsExpanded, setUpcomingCallsExpanded] = useState(true);

    // Driver list panel state
    const [driverListOpen, setDriverListOpen] = useState(false);
    const [driverSearchQuery, setDriverSearchQuery] = useState('');

    // Track drivers with upcoming rides (30 min reminder)
    // Map of driverId -> { rideId, scheduledFor, pickup, dropOff, minutesUntil }
    const [driversWithUpcomingRides, setDriversWithUpcomingRides] = useState({});

    // Today's rides state
    const [todaysRides, setTodaysRides] = useState([]);
    const [showRideMarkers, setShowRideMarkers] = useState(true);
    const [rideLocations, setRideLocations] = useState({}); // Map of rideId -> { lat, lng }
    const [selectedRide, setSelectedRide] = useState(null);
    const [ridePanelOpen, setRidePanelOpen] = useState(false);
    const [rideDirections, setRideDirections] = useState(null); // For mini map route
    const [mainMapDirections, setMainMapDirections] = useState(null); // For main map route
    const [highlightedDriverId, setHighlightedDriverId] = useState(null); // Highlight driver on main map
    const [driverDistanceInfo, setDriverDistanceInfo] = useState(null); // Distance/duration from driver to pickup
    const [hoveredRide, setHoveredRide] = useState(null);
    const [trackingLostAlerts, setTrackingLostAlerts] = useState([]); // Alerts when tracking is lost mid-ride
    const [refreshingLocations, setRefreshingLocations] = useState(false); // GPS refresh in progress
    const geocoderRef = useRef(null);
    // Persistent address → {lat,lng} cache; survives re-renders and polling cycles.
    // Keyed by normalized address so the same pickup shared by multiple rides is geocoded once.
    const geocodeCacheRef = useRef({});

    // Store initial center - map won't auto-recenter after first load
    const { settings } = useSettings();
    const markerColors = getSettingsMapMarkerColors(settings);
    const rideStatusColors = getSettingsMapRideStatusColors(settings);
    const initialCenter = useRef(getMapCenter(settings));
    const initialZoom = useRef(getMapZoom(settings));
    const mapRef = useRef(null);
    // Store selected driver ID to keep panel open on refresh
    const selectedDriverIdRef = useRef(null);

    // Load drivers on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                // Fetch driving, online drivers, and tracking lost alerts in parallel
                const [drivingData, onlineData, trackingAlerts] = await Promise.all([
                    driversAPI.getDriversDriving().catch(() => []),
                    driversAPI.getOnlineDrivers().catch(() => []),
                    driversAPI.getTrackingLostAlerts().catch(() => [])
                ]);

                // Restore any tracking lost alerts (persists across refresh)
                if (trackingAlerts && trackingAlerts.length > 0) {
                    setTrackingLostAlerts(prev => {
                        // Merge with any existing alerts, avoiding duplicates
                        const existingIds = new Set(prev.map(a => `${a.driverId}-${a.rideId}`));
                        const newAlerts = trackingAlerts
                            .filter(a => !existingIds.has(`${a.driverId}-${a.rideId}`))
                            .map(a => ({ ...a, timestamp: new Date(a.lastLocationTime) }));
                        return [...prev, ...newAlerts];
                    });
                }

                // Deduplicate driving drivers by ID (in case API returns duplicates)
                const uniqueDrivingDrivers = [];
                const seenDrivingIds = new Set();
                (drivingData || []).forEach(driver => {
                    const id = driver.driver?.id || driver.driverId || driver.id;
                    if (id && !seenDrivingIds.has(id)) {
                        seenDrivingIds.add(id);
                        uniqueDrivingDrivers.push(driver);
                    }
                });

                setDrivingDrivers(uniqueDrivingDrivers);
                setOnlineDrivers(onlineData || []);

                // If a driver panel is open, update it with fresh data
                if (selectedDriverIdRef.current) {
                    const allDrivers = [...(drivingData || []), ...(onlineData || [])];
                    const updatedDriver = allDrivers.find(d =>
                        (d.driver?.id || d.driverId || d.id) === selectedDriverIdRef.current
                    );
                    if (updatedDriver) {
                        setSidePanelDriver(prev => ({
                            ...updatedDriver,
                            isAvailable: prev?.isAvailable ?? !(drivingData || []).some(d =>
                                (d.driver?.id || d.driverId || d.id) === selectedDriverIdRef.current
                            )
                        }));
                    }
                }

                // Also try to get any existing locations from SignalR
                if (signalRConnection) {
                    try {
                        const result = await safeInvoke({
                            connection: signalRConnection,
                            connectFn: connectSignalR,
                            methodName: 'GetAllDriverLocations',
                            args: []
                        });
                        if (result.success) {
                            const locationsMap = {};
                            (result.result || []).forEach(loc => {
                                locationsMap[loc.driverId] = loc;
                            });
                            setDriverLocations(locationsMap);
                        }
                    } catch (err) {
                    }
                } else {
                }
            } catch (error) {
                console.error('Failed to load data:', error);
            } finally {
                // Only set loading to false on initial load, not on refreshes
                if (initialLoading) {
                    setInitialLoading(false);
                }
            }
        };

        loadData();

        // Refresh data every 30 seconds
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
    }, [signalRConnection]);

    // Handler to request GPS location from all drivers via SignalR/push notifications
    const handleRefreshDriverLocations = async () => {
        if (!signalRConnection || refreshingLocations) return;

        setRefreshingLocations(true);
        try {
            const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'RequestAllDriverLocations',
                args: [],
                maxRetries: 0, // triggers push to drivers — non-idempotent
            });

            if (result.success) {
                console.log('✅ GPS refresh requested for all drivers');
            } else {
                console.warn('⚠️ GPS refresh request failed:', result.error);
            }
        } catch (err) {
            console.error('Failed to request driver locations:', err);
        } finally {
            // Keep spinner for 3 seconds to indicate request is in progress
            setTimeout(() => setRefreshingLocations(false), 3000);
        }
    };

    // Subscribe to SignalR location updates and ride status changes
    useEffect(() => {
        if (!signalRConnection) return;

        // Refetch driver lists when needed
        const refreshDriverLists = async () => {
            try {
                const [drivingData, onlineData] = await Promise.all([
                    driversAPI.getDriversDriving().catch(() => []),
                    driversAPI.getOnlineDrivers().catch(() => [])
                ]);

                // Deduplicate driving drivers by ID (in case API returns duplicates)
                const uniqueDrivingDrivers = [];
                const seenDrivingIds = new Set();
                (drivingData || []).forEach(driver => {
                    const id = driver.driver?.id || driver.driverId || driver.id;
                    if (id && !seenDrivingIds.has(id)) {
                        seenDrivingIds.add(id);
                        uniqueDrivingDrivers.push(driver);
                    }
                });

                setDrivingDrivers(uniqueDrivingDrivers);
                setOnlineDrivers(onlineData || []);
            } catch (err) {
                console.error('Failed to refresh driver lists:', err);
            }
        };

        const handleLocationUpdated = (location) => {
            setDriverLocations(prev => ({
                ...prev,
                [location.driverId]: location
            }));
        };

        const handleLocationRemoved = (driverId) => {
            setDriverLocations(prev => {
                const newLocations = { ...prev };
                delete newLocations[driverId];
                return newLocations;
            });
        };

        // When a call is updated (assigned, completed, etc.), refresh the driver lists
        const handleCallUpdated = (data) => {
            refreshDriverLists();
        };

        // When a call is assigned to a driver
        const handleCallAssigned = (data) => {
            refreshDriverLists();
        };

        // When a ride is completed (dropoff clicked)
        const handleRideCompleted = (data) => {
            // Clear any tracking lost alerts for this ride
            if (data?.rideId) {
                setTrackingLostAlerts(prev => prev.filter(alert => alert.rideId !== data.rideId));
            }
            if (data?.driverId) {
                setTrackingLostAlerts(prev => prev.filter(alert => alert.driverId !== data.driverId));
            }
            refreshDriverLists();
        };

        // When a driver has an upcoming ride (30 min reminder)
        const handleDriverHasUpcomingRide = (data) => {
            console.log('Driver has upcoming ride:', data);
            setDriversWithUpcomingRides(prev => ({
                ...prev,
                [data.driverId]: {
                    rideId: data.rideId,
                    scheduledFor: data.scheduledFor,
                    pickup: data.pickup,
                    dropOff: data.dropOff,
                    minutesUntil: data.minutesUntil,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    hasLocation: data.hasLocation
                }
            }));

            // If we have the driver's location, update the driverLocations state
            if (data.hasLocation && data.latitude && data.longitude) {
                setDriverLocations(prev => ({
                    ...prev,
                    [data.driverId]: {
                        driverId: data.driverId,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        lastUpdate: new Date().toISOString()
                    }
                }));
            }

            // Auto-clear after 35 minutes (ride should have started by then)
            setTimeout(() => {
                setDriversWithUpcomingRides(prev => {
                    const newState = { ...prev };
                    delete newState[data.driverId];
                    return newState;
                });
            }, 35 * 60 * 1000);
        };

        // Subscribe to location events
        signalRConnection.on('DriverLocationUpdated', handleLocationUpdated);
        signalRConnection.on('DriverLocationRemoved', handleLocationRemoved);

        // Subscribe to ride status events
        signalRConnection.on('CallUpdated', handleCallUpdated);
        signalRConnection.on('CallAssigned', handleCallAssigned);
        signalRConnection.on('RideCompleted', handleRideCompleted);
        signalRConnection.on('CallDroppedOff', handleRideCompleted);
        signalRConnection.on('DriverHasUpcomingRide', handleDriverHasUpcomingRide);

        // Handle tracking lost alerts for active rides
        const handleTrackingLostAlert = (alert) => {
            console.warn('🚨 Tracking lost alert:', alert);
            setTrackingLostAlerts(prev => {
                // Prevent duplicate alerts
                const exists = prev.some(a => a.driverId === alert.driverId && a.rideId === alert.rideId);
                if (exists) return prev;
                return [...prev, { ...alert, timestamp: new Date() }];
            });
        };

        signalRConnection.on('TrackingLostAlert', handleTrackingLostAlert);

        return () => {
            // Unsubscribe when component unmounts or connection changes
            signalRConnection.off('DriverLocationUpdated', handleLocationUpdated);
            signalRConnection.off('DriverLocationRemoved', handleLocationRemoved);
            signalRConnection.off('CallUpdated', handleCallUpdated);
            signalRConnection.off('CallAssigned', handleCallAssigned);
            signalRConnection.off('RideCompleted', handleRideCompleted);
            signalRConnection.off('CallDroppedOff', handleRideCompleted);
            signalRConnection.off('DriverHasUpcomingRide', handleDriverHasUpcomingRide);
            signalRConnection.off('TrackingLostAlert', handleTrackingLostAlert);
        };
    }, [signalRConnection]);

    // Load today's rides and geocode pickup addresses
    useEffect(() => {
        const loadTodaysRides = async () => {
            try {
                const rides = await dashboardAPI.getTodaysRides();
                // Filter out canceled rides
                const activeRides = (rides || []).filter(ride => !ride.canceled);
                setTodaysRides(activeRides);

                // Geocode pickup addresses
                if (window.google && activeRides.length > 0) {
                    if (!geocoderRef.current) {
                        geocoderRef.current = new window.google.maps.Geocoder();
                    }

                    // Geocode in batches to avoid rate limiting.
                    // geocodeCacheRef persists across polls; each unique address is geocoded once per session.
                    const batchSize = 10;
                    const newLocations = {};

                    for (let i = 0; i < activeRides.length; i += batchSize) {
                        const batch = activeRides.slice(i, i + batchSize);
                        await Promise.all(batch.map(async (ride) => {
                            if (!ride.route?.pickup) return;
                            const normalizedAddress = ride.route.pickup.trim().toLowerCase();
                            if (geocodeCacheRef.current[normalizedAddress]) {
                                // Cache hit — reuse without an API call
                                newLocations[ride.rideId] = geocodeCacheRef.current[normalizedAddress];
                                return;
                            }
                            try {
                                const result = await new Promise((resolve, reject) => {
                                    geocoderRef.current.geocode(
                                        { address: ride.route.pickup },
                                        (results, status) => {
                                            if (status === 'OK' && results[0]) {
                                                resolve(results[0].geometry.location);
                                            } else {
                                                reject(new Error(status));
                                            }
                                        }
                                    );
                                });
                                const coords = { lat: result.lat(), lng: result.lng() };
                                geocodeCacheRef.current[normalizedAddress] = coords;
                                newLocations[ride.rideId] = coords;
                            } catch (err) {
                                // Skip rides that can't be geocoded
                                console.warn(`Failed to geocode ride ${ride.rideId}:`, err.message);
                            }
                        }));
                        // Small delay between batches
                        if (i + batchSize < activeRides.length) {
                            await new Promise(resolve => setTimeout(resolve, 200));
                        }
                    }

                    setRideLocations(prev => ({ ...prev, ...newLocations }));
                }
            } catch (error) {
                console.error('Failed to load today\'s rides:', error);
            }
        };

        loadTodaysRides();

        // Refresh rides every 2 minutes
        const interval = setInterval(loadTodaysRides, 120000);
        return () => clearInterval(interval);
    }, []);

    // Calculate directions for ride mini map when a ride is selected
    useEffect(() => {
        if (!selectedRide || !window.google) {
            setRideDirections(null);
            setMainMapDirections(null);
            setHighlightedDriverId(null);
            setDriverDistanceInfo(null);
            return;
        }

        // Highlight the assigned driver
        const assignedDriverId = selectedRide.reassigned
            ? selectedRide.reassignedToId
            : selectedRide.assignedToId;
        setHighlightedDriverId(assignedDriverId || null);

        const calculateRoute = async () => {
            const directionsService = new window.google.maps.DirectionsService();

            const origin = selectedRide.route?.pickup;
            const destination = selectedRide.route?.dropOff;

            if (!origin || !destination) {
                setRideDirections(null);
                setMainMapDirections(null);
                return;
            }

            // Build waypoints from stops
            const waypoints = [];
            const stopFields = ['stop1', 'stop2', 'stop3', 'stop4', 'stop5', 'stop6', 'stop7', 'stop8', 'stop9', 'stop10'];
            stopFields.forEach(field => {
                if (selectedRide.route[field]) {
                    waypoints.push({
                        location: selectedRide.route[field],
                        stopover: true
                    });
                }
            });

            try {
                const result = await directionsService.route({
                    origin,
                    destination: selectedRide.route.roundTrip ? origin : destination,
                    waypoints: selectedRide.route.roundTrip
                        ? [...waypoints, { location: destination, stopover: true }]
                        : waypoints,
                    travelMode: window.google.maps.TravelMode.DRIVING
                });
                setRideDirections(result);
                setMainMapDirections(result); // Also show on main map
            } catch (err) {
                console.error('Failed to calculate route:', err);
                setRideDirections(null);
                setMainMapDirections(null);
            }
        };

        calculateRoute();
    }, [selectedRide]);

    // Calculate driver distance to pickup when ride is selected
    useEffect(() => {
        if (!selectedRide || !window.google) {
            setDriverDistanceInfo(null);
            return;
        }

        const assignedDriverId = selectedRide.reassigned
            ? selectedRide.reassignedToId
            : selectedRide.assignedToId;

        if (!assignedDriverId) {
            setDriverDistanceInfo(null);
            return;
        }

        // Find driver's current location
        const driverData = drivingDrivers.find(d => {
            const id = d.driver?.id || d.driverId || d.id;
            return id === assignedDriverId;
        }) || onlineDrivers.find(d => {
            const id = d.driver?.id || d.driverId || d.id;
            return id === assignedDriverId;
        });

        const driverPosition = driverData ? getDriverPosition(driverData) : null;
        const pickupLocation = rideLocations[selectedRide.rideId];

        if (!driverPosition || !pickupLocation) {
            setDriverDistanceInfo(null);
            return;
        }

        // Calculate distance from driver to pickup
        const calculateDriverDistance = async () => {
            try {
                const service = new window.google.maps.DistanceMatrixService();
                const result = await service.getDistanceMatrix({
                    origins: [driverPosition],
                    destinations: [pickupLocation],
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    unitSystem: window.google.maps.UnitSystem.IMPERIAL
                });

                if (result.rows[0]?.elements[0]?.status === 'OK') {
                    const element = result.rows[0].elements[0];
                    setDriverDistanceInfo({
                        distance: element.distance.text,
                        duration: element.duration.text
                    });
                } else {
                    setDriverDistanceInfo(null);
                }
            } catch (err) {
                console.error('Failed to calculate driver distance:', err);
                setDriverDistanceInfo(null);
            }
        };

        calculateDriverDistance();
    }, [selectedRide, drivingDrivers, onlineDrivers, rideLocations]);

    // Called when the map loads - don't auto-center after initial load
    const onLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    // Called when the map unmounts
    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    // Handle marker click - open side panel
    const handleMarkerClick = (driver, isAvailable = false) => {
        const driverId = driver.driver?.id || driver.driverId || driver.id;
        selectedDriverIdRef.current = driverId;
        setSidePanelDriver({ ...driver, isAvailable });
        setSidePanelOpen(true);
    };

    // Handle marker hover
    const handleMarkerMouseOver = (driver, isAvailable = false) => {
        setHoveredDriver({ ...driver, isAvailable });
    };

    const handleMarkerMouseOut = () => {
        setHoveredDriver(null);
    };

    // Close side panel
    const handleCloseSidePanel = () => {
        selectedDriverIdRef.current = null;
        setSidePanelOpen(false);
        setSidePanelDriver(null);
    };

    // Open/close driver list panel
    const handleToggleDriverList = () => {
        setDriverListOpen(!driverListOpen);
        setDriverSearchQuery(''); // Clear search when opening/closing
    };

    // Smooth pan and zoom animation helper
    const smoothPanAndZoom = (targetPosition, targetZoom = 16, duration = 1000) => {
        if (!map) return;

        const startPosition = map.getCenter();
        const startZoom = map.getZoom();
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out cubic for smooth deceleration
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            // Interpolate position
            const currentLat = startPosition.lat() + (targetPosition.lat - startPosition.lat()) * easeProgress;
            const currentLng = startPosition.lng() + (targetPosition.lng - startPosition.lng()) * easeProgress;

            // Interpolate zoom
            const currentZoom = startZoom + (targetZoom - startZoom) * easeProgress;

            map.setCenter({ lat: currentLat, lng: currentLng });
            map.setZoom(currentZoom);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    };

    // Focus map on a specific driver's location with smooth animation
    const handleFocusOnDriver = (driverData) => {
        const position = getDriverPosition(driverData);
        if (position && map) {
            smoothPanAndZoom(position, 16, 800);
            setDriverListOpen(false);
            setDriverSearchQuery('');
        }
    };

    // Get all drivers with their availability status combined
    const getAllDriversForList = () => {
        const drivingDriverIds = new Set(drivingDrivers.map(d => getDriverId(d)));
        const uniqueOnlineDrivers = onlineDrivers.filter(d => !drivingDriverIds.has(getDriverId(d)));

        // Combine both lists with status indicator
        const allDrivers = [
            ...drivingDrivers.map(d => ({ ...d, statusType: 'driving' })),
            ...uniqueOnlineDrivers.map(d => ({ ...d, statusType: 'available' }))
        ];

        // Filter by search query
        if (driverSearchQuery.trim()) {
            const query = driverSearchQuery.toLowerCase().trim();
            return allDrivers.filter(d => {
                const name = (d.driver?.name || '').toLowerCase();
                return name.includes(query);
            });
        }

        return allDrivers;
    };

    // Helper to get route stops with dynamic labels and colors (supports up to 10 stops)
    const getRouteStops = (route, isRoundTrip) => {
        if (!route) return [];
        const stops = [];
        // Extended color palette for up to 12 waypoints (pickup + 10 stops + dropoff)
        const colors = [
            '#4caf50', // A - Pickup (green)
            '#8bc34a', // B - Stop 1 (light green)
            '#cddc39', // C - Stop 2 (lime)
            '#ffeb3b', // D - Stop 3 (yellow)
            '#ffc107', // E - Stop 4 (amber)
            '#ff9800', // F - Stop 5 (orange)
            '#ff5722', // G - Stop 6 (deep orange)
            '#e91e63', // H - Stop 7 (pink)
            '#9c27b0', // I - Stop 8 (purple)
            '#673ab7', // J - Stop 9 (deep purple)
            '#3f51b5', // K - Stop 10 (indigo)
            '#f44336'  // Last - Dropoff/Return (red)
        ];
        let labelIndex = 0;

        // Pickup is always A (green)
        stops.push({ label: 'A', address: route.pickup, type: 'Pickup', color: colors[0] });
        labelIndex++;

        // Add stops B, C, D... up to stop 10
        const stopFields = ['stop1', 'stop2', 'stop3', 'stop4', 'stop5', 'stop6', 'stop7', 'stop8', 'stop9', 'stop10'];
        stopFields.forEach((field, idx) => {
            if (route[field]) {
                stops.push({
                    label: String.fromCharCode(65 + labelIndex),
                    address: route[field],
                    type: `Stop ${idx + 1}`,
                    color: colors[Math.min(labelIndex, colors.length - 2)]
                });
                labelIndex++;
            }
        });

        // Dropoff
        stops.push({
            label: String.fromCharCode(65 + labelIndex),
            address: route.dropOff,
            type: 'Drop-off',
            color: isRoundTrip ? colors[Math.min(labelIndex, colors.length - 2)] : '#f44336'
        });
        labelIndex++;

        // Round trip - pickup is last stop and should be red
        if (isRoundTrip) {
            stops.push({
                label: String.fromCharCode(65 + labelIndex),
                address: route.pickup,
                type: 'Return (Round Trip)',
                color: '#f44336',
                isRoundTrip: true
            });
        }

        return stops;
    };

    // Get stop count for a ride (supports up to 10 stops)
    const getStopCount = (route) => {
        return [
            route?.stop1, route?.stop2, route?.stop3, route?.stop4, route?.stop5,
            route?.stop6, route?.stop7, route?.stop8, route?.stop9, route?.stop10
        ].filter(stop => stop && stop.trim() !== '').length;
    };

    // Filter upcoming rides to exclude current ride
    const getFilteredUpcomingRides = (upcomingRides, currentRideId) => {
        if (!upcomingRides) return [];
        return upcomingRides.filter(ride => ride.rideId !== currentRideId);
    };

    // Get driver position from location updates
    const getDriverPosition = (driverData) => {
        // DriverRideShare has driver object nested, or it could be passed directly
        const driverId = driverData.driver?.id || driverData.driverId || driverData.id;
        const location = driverLocations[driverId];
        if (location && location.latitude && location.longitude) {
            return { lat: location.latitude, lng: location.longitude };
        }
        return null;
    };

    // Helper to get driver ID from DriverRideShare or direct driver object
    const getDriverId = (driverData) => {
        return driverData.driver?.id || driverData.driverId || driverData.id;
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'N/A';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    // Format duration
    const formatDuration = (duration) => {
        if (!duration) return 'N/A';
        const parts = duration.split(':');
        if (parts.length === 3) {
            const hours = parseInt(parts[0]);
            const minutes = parseInt(parts[1]);
            if (hours > 0) {
                return `${hours}h ${minutes}m`;
            }
            return `${minutes}m`;
        }
        return duration;
    };

    // Show error if Google Maps failed to load
    if (loadError) {
        return (
            <Box p={3}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="error" variant="h6" gutterBottom>
                        Failed to load Google Maps
                    </Typography>
                    <Typography color="text.secondary">
                        Please check your API key in src/config/googleMapsConfig.js
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // Show loading state only on initial load
    if (!isLoaded || initialLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="500px"
            >
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    Loading map...
                </Typography>
            </Box>
        );
    }

    // Count drivers with known locations
    const drivingWithLocations = drivingDrivers.filter(d => getDriverPosition(d) !== null);

    // Filter out online drivers that are already in the driving list to prevent duplicate keys
    const drivingDriverIds = new Set(drivingDrivers.map(d => getDriverId(d)));
    const uniqueOnlineDrivers = onlineDrivers.filter(d => !drivingDriverIds.has(getDriverId(d)));
    const onlineWithLocations = uniqueOnlineDrivers.filter(d => getDriverPosition(d) !== null);

    const totalOnMap = drivingWithLocations.length + onlineWithLocations.length;

    // Dismiss a tracking lost alert
    const dismissAlert = (driverId, rideId) => {
        setTrackingLostAlerts(prev => prev.filter(a => !(a.driverId === driverId && a.rideId === rideId)));
    };

    return (
        <Box>
            {/* Tracking Lost Alerts - Show prominently at top */}
            {trackingLostAlerts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    {trackingLostAlerts.map((alert, idx) => (
                        <Paper
                            key={`alert-${alert.driverId}-${alert.rideId}-${idx}`}
                            elevation={4}
                            sx={{
                                p: 2,
                                mb: 1,
                                bgcolor: '#d32f2f',
                                border: '2px solid',
                                borderColor: '#b71c1c',
                                borderRadius: 2,
                                animation: 'pulse 2s infinite'
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
                                        🚨 TRACKING LOST - Driver Not Responding
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                                        <strong>{alert.driverName}</strong> has not sent location for <strong>{alert.minutesSinceUpdate}+ minutes</strong> during Ride #{alert.rideId}
                                    </Typography>
                                    {alert.customerName && (
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                            Customer: {alert.customerName}
                                        </Typography>
                                    )}
                                </Box>
                                <Box display="flex" gap={1}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        sx={{ bgcolor: 'white', color: '#d32f2f', '&:hover': { bgcolor: '#f5f5f5' } }}
                                        onClick={() => {
                                            // Open messaging modal to contact driver
                                            if (openMessagingModal) {
                                                openMessagingModal(alert.driverId);
                                            }
                                        }}
                                    >
                                        📞 CONTACT DRIVER
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderColor: 'white', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'white' } }}
                                        onClick={() => dismissAlert(alert.driverId, alert.rideId)}
                                    >
                                        Dismiss
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* Top Controls Row */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                {/* Toggle on left */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={showRideMarkers}
                            onChange={(e) => setShowRideMarkers(e.target.checked)}
                            color="primary"
                        />
                    }
                    label={
                        <Box display="flex" alignItems="center" gap={1}>
                            <MapIcon fontSize="small" />
                            <Typography variant="body2">Show Today's Rides ({todaysRides.length})</Typography>
                        </Box>
                    }
                />

                {/* Badges and Driver List Button on right */}
                <Box display="flex" gap={1} alignItems="center">
                    <Tooltip title="View Driver List">
                        <IconButton
                            onClick={handleToggleDriverList}
                            color={driverListOpen ? "primary" : "default"}
                            sx={{
                                border: '1px solid',
                                borderColor: driverListOpen ? 'primary.main' : 'divider',
                                bgcolor: driverListOpen ? 'primary.50' : 'transparent'
                            }}
                        >
                            <ListIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Drivers on active calls">
                        <Badge
                            badgeContent={drivingDrivers.length}
                            sx={{ '& .MuiBadge-badge': { bgcolor: '#b10606', color: 'white' } }}
                        >
                            <CarIcon fontSize="large" />
                        </Badge>
                    </Tooltip>
                    <Tooltip title="Available drivers">
                        <Badge
                            badgeContent={uniqueOnlineDrivers.length}
                            sx={{ '& .MuiBadge-badge': { bgcolor: '#0a9702', color: 'white' } }}
                        >
                            <PersonIcon fontSize="large" />
                        </Badge>
                    </Tooltip>
                    {Object.keys(driversWithUpcomingRides).length > 0 && (
                        <Tooltip title="Drivers with upcoming pickups (within 30 min)">
                            <Badge
                                badgeContent={Object.keys(driversWithUpcomingRides).length}
                                sx={{ '& .MuiBadge-badge': { bgcolor: '#FF9800', color: 'white' } }}
                            >
                                <ScheduleIcon fontSize="large" sx={{ color: '#FF9800' }} />
                            </Badge>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Map with Legends on Left */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Legends Panel - Left Side */}
                <Box sx={{ width: 160, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Driver Legend */}
                    <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                Drivers ({drivingDrivers.length + uniqueOnlineDrivers.length})
                            </Typography>
                            <Tooltip title="Request GPS from all drivers">
                                <IconButton
                                    size="small"
                                    onClick={handleRefreshDriverLocations}
                                    disabled={refreshingLocations}
                                    sx={{ p: 0.25 }}
                                >
                                    <RefreshIcon
                                        fontSize="small"
                                        sx={{
                                            animation: refreshingLocations ? 'spin 1s linear infinite' : 'none',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                            }
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.75}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 10, height: 10, bgcolor: '#b10606', borderRadius: '50%' }} />
                                <Typography variant="caption" color="text.secondary">Driving (Online)</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 10, height: 10, bgcolor: '#FFD700', borderRadius: '50%' }} />
                                <Typography variant="caption" color="text.secondary">Driving (Offline)</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 10, height: 10, bgcolor: '#0a9702', borderRadius: '50%' }} />
                                <Typography variant="caption" color="text.secondary">Available</Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Rides Legend - only show when toggle is on */}
                    {showRideMarkers && (
                        <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                                Rides ({todaysRides.length})
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={0.75}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 10, height: 10, bgcolor: '#f44336', borderRadius: '50%' }} />
                                    <Typography variant="caption" color="text.secondary">Missed/Late</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 10, height: 10, bgcolor: '#ffeb3b', borderRadius: '50%', border: '1px solid #000' }} />
                                    <Typography variant="caption" color="text.secondary">In Progress</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 10, height: 10, bgcolor: '#4caf50', borderRadius: '50%' }} />
                                    <Typography variant="caption" color="text.secondary">Upcoming</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 10, height: 10, bgcolor: '#9e9e9e', borderRadius: '50%', opacity: 0.6 }} />
                                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </Box>

                {/* Map Container */}
                <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2, flex: 1 }}>
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={initialCenter.current}
                        zoom={initialZoom.current}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={mapOptions}
                    >
                        {/* Main Map Route for Selected Ride */}
                        {mainMapDirections && (
                            <DirectionsRenderer
                                directions={mainMapDirections}
                                options={{
                                    suppressMarkers: true, // Don't show A/B markers, we have our own
                                    polylineOptions: {
                                        strokeColor: '#2196F3',
                                        strokeWeight: 4,
                                        strokeOpacity: 0.6,
                                        zIndex: 1 // Below markers
                                    }
                                }}
                            />
                        )}

                        {/* Driving Driver Markers (blue/gray or highlighted) */}
                        {drivingDrivers.map(driverData => {
                            const position = getDriverPosition(driverData);
                            if (!position) return null;
                            const isOnline = driverData.isOnline;
                            const driverId = getDriverId(driverData);
                            const isHighlighted = highlightedDriverId === driverId;

                            return (
                                <Marker
                                    key={`driving-${driverId}`}
                                    position={position}
                                    icon={isHighlighted ? getHighlightedDriverIcon(markerColors) : getDrivingDriverIcon(isOnline, markerColors)}
                                    onClick={() => handleMarkerClick(driverData, false)}
                                    onMouseOver={() => handleMarkerMouseOver(driverData, false)}
                                    onMouseOut={handleMarkerMouseOut}
                                    zIndex={isHighlighted ? 1000 : 1}
                                />
                            );
                        })}

                        {/* Available Driver Markers (green, orange, or highlighted) */}
                        {uniqueOnlineDrivers.map(driverData => {
                            const position = getDriverPosition(driverData);
                            if (!position) return null;
                            const driverId = getDriverId(driverData);
                            const hasUpcomingRide = driversWithUpcomingRides[driverId];
                            const isHighlighted = highlightedDriverId === driverId;

                            return (
                                <Marker
                                    key={`available-${driverId}`}
                                    position={position}
                                    icon={isHighlighted ? getHighlightedDriverIcon(markerColors) : (hasUpcomingRide ? getUpcomingRideDriverIcon(markerColors) : getAvailableDriverIcon(markerColors))}
                                    onClick={() => handleMarkerClick(driverData, true)}
                                    onMouseOver={() => handleMarkerMouseOver(driverData, true)}
                                    onMouseOut={handleMarkerMouseOut}
                                    zIndex={isHighlighted ? 1000 : 1}
                                />
                            );
                        })}

                        {/* Hover InfoWindow - no close button */}
                        {hoveredDriver && getDriverPosition(hoveredDriver) && (
                            <InfoWindow
                                position={getDriverPosition(hoveredDriver)}
                                options={{
                                    pixelOffset: new window.google.maps.Size(0, -35),
                                    disableAutoPan: true,
                                    maxWidth: 250
                                }}
                            >
                                <Box sx={{ minWidth: 150, p: 0 }}>
                                    <style>{`.gm-ui-hover-effect { display: none !important; }`}</style>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {hoveredDriver.driver?.name}
                                        </Typography>
                                        {hoveredDriver.isOnline !== undefined && (
                                            hoveredDriver.isOnline ?
                                                <OnlineIcon sx={{ fontSize: 14, color: 'success.main' }} /> :
                                                <OfflineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        )}
                                    </Box>
                                    <Chip
                                        label={hoveredDriver.isAvailable ? 'Available' : 'On Call'}
                                        size="small"
                                        color={hoveredDriver.isAvailable ? 'success' : 'primary'}
                                        sx={{ mb: 0.5 }}
                                    />
                                    {/* Last seen indicator */}
                                    {(() => {
                                        const driverId = hoveredDriver.driver?.id || hoveredDriver.driverId || hoveredDriver.id;
                                        const location = driverLocations[driverId];
                                        if (location?.lastUpdate) {
                                            const lastSeen = formatLastSeen(location.lastUpdate);
                                            return (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color: lastSeen.isVeryStale ? 'error.main' : lastSeen.isStale ? 'warning.main' : 'success.main',
                                                        fontWeight: lastSeen.isStale ? 'bold' : 'normal'
                                                    }}
                                                >
                                                    {lastSeen.isVeryStale ? '⚠️ ' : lastSeen.isStale ? '⏱️ ' : '📍 '}
                                                    Last seen: {lastSeen.text}
                                                </Typography>
                                            );
                                        }
                                        return null;
                                    })()}
                                    {hoveredDriver.primaryCar && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {hoveredDriver.primaryCar.color} {hoveredDriver.primaryCar.make} {hoveredDriver.primaryCar.model}
                                        </Typography>
                                    )}
                                    {hoveredDriver.currentRide && (
                                        <Typography variant="caption" color="primary" display="block">
                                            Heading to {hoveredDriver.currentRide.route?.dropOff || 'Unknown destination'}
                                        </Typography>
                                    )}
                                    {/* Show upcoming ride alert if applicable */}
                                    {(() => {
                                        const driverId = hoveredDriver.driver?.id || hoveredDriver.driverId || hoveredDriver.id;
                                        const upcomingRide = driversWithUpcomingRides[driverId];
                                        if (upcomingRide) {
                                            return (
                                                <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                                                    <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block">
                                                        ⏰ Pickup in ~{upcomingRide.minutesUntil} min
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                                                        {upcomingRide.pickup}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    })()}
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Click for details
                                    </Typography>
                                </Box>
                            </InfoWindow>
                        )}

                        {/* Ride Pickup Location Markers */}
                        {showRideMarkers && todaysRides.map(ride => {
                            const position = rideLocations[ride.rideId];
                            if (!position) return null;
                            const status = getRideStatus(ride);

                            return (
                                <Marker
                                    key={`ride-${ride.rideId}`}
                                    position={position}
                                    icon={getRideIcon(status, markerColors)}
                                    onClick={() => {
                                        setSelectedRide(ride);
                                        setRidePanelOpen(true);
                                    }}
                                    onMouseOver={() => setHoveredRide(ride)}
                                    onMouseOut={() => setHoveredRide(null)}
                                />
                            );
                        })}

                        {/* Ride Hover InfoWindow */}
                        {hoveredRide && rideLocations[hoveredRide.rideId] && (
                            <InfoWindow
                                position={rideLocations[hoveredRide.rideId]}
                                options={{
                                    pixelOffset: new window.google.maps.Size(0, -35),
                                    disableAutoPan: true,
                                    maxWidth: 280
                                }}
                            >
                                <Box sx={{ minWidth: 180, p: 0 }}>
                                    <style>{`.gm-ui-hover-effect { display: none !important; }`}</style>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Chip
                                            label={getRideStatusLabel(getRideStatus(hoveredRide))}
                                            size="small"
                                            sx={{
                                                bgcolor: getRideStatusColor(getRideStatus(hoveredRide), rideStatusColors),
                                                color: getRideStatus(hoveredRide) === 'inProgress' ? 'black' : 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            #{hoveredRide.rideId}
                                        </Typography>
                                    </Box>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        {hoveredRide.customerName || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        📍 {hoveredRide.route?.pickup}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        🎯 {hoveredRide.route?.dropOff}
                                    </Typography>
                                    <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                                        🕐 {hoveredRide.scheduledFor ? formatTime(hoveredRide.scheduledFor) : 'ASAP'}
                                    </Typography>
                                    {(hoveredRide.reassigned ? hoveredRide.reassignedTo : hoveredRide.assignedTo) && (
                                        <Typography variant="caption" color="success.main" display="block">
                                            👤 {hoveredRide.reassigned ? hoveredRide.reassignedTo?.name : hoveredRide.assignedTo?.name}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                                        Click for details
                                    </Typography>
                                </Box>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </Paper>
            </Box>

            {/* Driver List Panel */}
            <Drawer
                anchor="right"
                open={driverListOpen}
                onClose={handleToggleDriverList}
            >
                <Box sx={{ width: 350, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Panel Header */}
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <ListIcon color="primary" />
                                <Typography variant="h6">All Drivers</Typography>
                            </Box>
                            <IconButton onClick={handleToggleDriverList}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by driver name..."
                            value={driverSearchQuery}
                            onChange={(e) => setDriverSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ bgcolor: 'background.paper' }}
                        />
                    </Box>

                    {/* Driver List */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <List>
                            {getAllDriversForList().length === 0 ? (
                                <ListItem>
                                    <ListItemText
                                        primary="No drivers found"
                                        secondary={driverSearchQuery ? "Try a different search term" : "No drivers are currently active"}
                                    />
                                </ListItem>
                            ) : (
                                getAllDriversForList().map((driverData) => {
                                    const driverId = getDriverId(driverData);
                                    // Check if we have actual GPS location from SignalR
                                    const location = driverLocations[driverId];
                                    const hasValidLocation = location && location.latitude && location.longitude;
                                    const isDriving = driverData.statusType === 'driving';
                                    const isAvailable = driverData.statusType === 'available';

                                    // Get last seen info if we have a location
                                    const lastSeen = hasValidLocation ? formatLastSeen(location.lastUpdate) : null;

                                    return (
                                        <ListItemButton
                                            key={`list-${driverId}`}
                                            onClick={() => {
                                                setDriverListOpen(false);
                                                setDriverSearchQuery('');
                                                handleMarkerClick(driverData, isAvailable);
                                                // Focus on driver location with smooth animation
                                                const position = getDriverPosition(driverData);
                                                if (position && map) {
                                                    smoothPanAndZoom(position, 16, 800);
                                                }
                                            }}
                                            sx={{
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <ListItemIcon>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: isDriving
                                                            ? (driverData.isOnline ? '#b10606' : '#FFD700')
                                                            : '#0a9702'
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={driverData.driver?.name || 'Unknown Driver'}
                                                secondary={
                                                    <Box component="span">
                                                        {isDriving ? 'On a call' : 'Available'}
                                                        {!hasValidLocation ? (
                                                            <Box component="span" sx={{ color: 'warning.main' }}> • No GPS</Box>
                                                        ) : lastSeen && (
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    color: lastSeen.isVeryStale ? 'error.main' : lastSeen.isStale ? 'warning.main' : 'text.secondary'
                                                                }}
                                                            >
                                                                {' • '}{lastSeen.isVeryStale ? '⚠️ ' : ''}{lastSeen.text}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    );
                                })
                            )}
                        </List>
                    </Box>

                    {/* Footer with count */}
                    <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {getAllDriversForList().length} driver{getAllDriversForList().length !== 1 ? 's' : ''}
                            {driverSearchQuery && ' matching search'}
                        </Typography>
                    </Box>
                </Box>
            </Drawer>

            {/* Driver Detail Side Panel - Only shown when a driver is clicked */}
            <Drawer
                anchor="right"
                open={sidePanelOpen}
                onClose={handleCloseSidePanel}
            >
                <Box sx={{ width: 400, p: 2 }}>
                    {/* Panel Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon color="primary" />
                            <Typography variant="h6">Driver Details</Typography>
                            {sidePanelDriver?.isAvailable ? (
                                <Chip label="Available" size="small" color="success" />
                            ) : (
                                <Chip label="On Call" size="small" color="primary" />
                            )}
                        </Box>
                        <IconButton onClick={handleCloseSidePanel}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {sidePanelDriver && (
                        <Box>
                            {/* Online Status and Message Button */}
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {(() => {
                                        const driverId = getDriverId(sidePanelDriver);
                                        const location = driverLocations[driverId];
                                        const lastUpdate = location?.lastUpdate ? new Date(location.lastUpdate) : null;
                                        const minutesAgo = lastUpdate ? Math.floor((Date.now() - lastUpdate.getTime()) / 60000) : null;

                                        // If they have a location update in the last 3 minutes, they're actively tracked
                                        const isActivelyTracked = lastUpdate && minutesAgo < 3;
                                        // If between 3-15 mins, show warning (stale tracking)
                                        const isStaleTracking = lastUpdate && minutesAgo >= 3 && minutesAgo < 15;

                                        if (isActivelyTracked) {
                                            return (
                                                <>
                                                    <OnlineIcon color="success" />
                                                    <Typography variant="body2" color="success.main">Online</Typography>
                                                </>
                                            );
                                        } else if (isStaleTracking) {
                                            return (
                                                <>
                                                    <OnlineIcon sx={{ color: '#FF9800' }} />
                                                    <Typography variant="body2" sx={{ color: '#FF9800' }}>
                                                        ⚠️ Last seen {minutesAgo} min ago
                                                    </Typography>
                                                </>
                                            );
                                        } else if (sidePanelDriver.isOnline) {
                                            // Has heartbeat but no recent location
                                            return (
                                                <>
                                                    <OnlineIcon sx={{ color: '#FF9800' }} />
                                                    <Typography variant="body2" sx={{ color: '#FF9800' }}>
                                                        Online (No GPS)
                                                    </Typography>
                                                </>
                                            );
                                        } else {
                                            return (
                                                <>
                                                    <OfflineIcon color="disabled" />
                                                    <Typography variant="body2" color="text.disabled">Offline</Typography>
                                                </>
                                            );
                                        }
                                    })()}
                                </Box>
                                {openMessagingModal && (
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<MessageIcon />}
                                            onClick={() => {
                                                const driverId = getDriverId(sidePanelDriver);
                                                if (driverId) {
                                                    openMessagingModal(driverId);
                                                }
                                            }}
                                        >
                                            Message
                                        </Button>
                                        <Tooltip title="Request driver to send their current location">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="warning"
                                                startIcon={<MyLocationIcon />}
                                                onClick={async () => {
                                                    const driverId = getDriverId(sidePanelDriver);
                                                    if (driverId && signalRConnection) {
                                                        try {
                                                            await safeInvoke({
                                                                connection: signalRConnection,
                                                                connectFn: connectSignalR,
                                                                methodName: 'RequestDriverLocation',
                                                                args: [driverId],
                                                                maxRetries: 0, // triggers push to driver — non-idempotent
                                                            });
                                                            console.log('Location request sent for driver', driverId);
                                                        } catch (err) {
                                                            console.error('Failed to request location:', err);
                                                        }
                                                    }
                                                }}
                                            >
                                                Location
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>

                            {/* Driver Info Section */}
                            <Card variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                                        Driver Information
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText
                                                primary="Name"
                                                secondary={sidePanelDriver.driver?.name || 'N/A'}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText
                                                primary="Phone"
                                                secondary={sidePanelDriver.driver?.phoneNumber || 'N/A'}
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>

                            {/* Car Info Section */}
                            {sidePanelDriver.primaryCar && (
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            <CarIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                                            Vehicle Information
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText
                                                    primary="Vehicle"
                                                    secondary={`${sidePanelDriver.primaryCar.year || ''} ${sidePanelDriver.primaryCar.make || ''} ${sidePanelDriver.primaryCar.model || ''}`}
                                                />
                                                <ListItemText
                                                    primary="License Plate"
                                                    secondary={sidePanelDriver.primaryCar.licensePlate || 'N/A'}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText
                                                    primary="Color"
                                                    secondary={sidePanelDriver.primaryCar.color || 'N/A'}
                                                />
                                                <ListItemText
                                                    primary="Seats"
                                                    secondary={sidePanelDriver.primaryCar.seats || 'N/A'}
                                                />
                                                <ListItemText
                                                    primary="Type"
                                                    secondary={sidePanelDriver.primaryCar.type || 'N/A'}
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Current Ride Section - Only for driving drivers */}
                            {!sidePanelDriver.isAvailable && sidePanelDriver.currentRide && (
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            <RouteIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                                            Current Ride
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Customer"
                                                    secondary={sidePanelDriver.currentRide.customerName || 'N/A'}
                                                />
                                                <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Customer Phone"
                                                    secondary={sidePanelDriver.currentRide.customerPhoneNumber || 'N/A'}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon><ScheduledIcon fontSize="small" color="primary" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Scheduled For"
                                                    secondary={formatTime(sidePanelDriver.currentRide.scheduledFor)}
                                                />
                                                <ListItemIcon><DurationIcon fontSize="small" color="info" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Est. Duration"
                                                    secondary={formatDuration(sidePanelDriver.currentRide.route?.estimatedDuration)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon><PickupTimeIcon fontSize="small" color="success" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Pickup Time"
                                                    secondary={formatTime(sidePanelDriver.currentRide.pickupTime)}
                                                />
                                                <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Passengers"
                                                    secondary={sidePanelDriver.currentRide.passengers || 'N/A'}
                                                />
                                            </ListItem>
                                            {isAdmin && (
                                                <ListItem>
                                                    <ListItemIcon><MoneyIcon fontSize="small" /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Cost"
                                                        secondary={formatCurrency(sidePanelDriver.currentRide.cost)}
                                                    />
                                                    <ListItemText
                                                        primary="Payment Status"
                                                        secondary={
                                                            <Chip
                                                                label={sidePanelDriver.currentRide.paid || sidePanelDriver.currentRide.paymentTokenId ? 'Prepaid' : 'Driver Collects'}
                                                                size="small"
                                                                color={sidePanelDriver.currentRide.paid || sidePanelDriver.currentRide.paymentTokenId ? 'success' : 'warning'}
                                                            />
                                                        }
                                                    />
                                                </ListItem>
                                            )}
                                            {sidePanelDriver.currentRide.notes && (
                                                <ListItem>
                                                    <ListItemIcon><NotesIcon fontSize="small" /></ListItemIcon>
                                                    <ListItemText
                                                        primary="Notes"
                                                        secondary={sidePanelDriver.currentRide.notes}
                                                    />
                                                </ListItem>
                                            )}
                                        </List>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Route Section - Only for driving drivers - Dynamic labels and colors */}
                            {!sidePanelDriver.isAvailable && sidePanelDriver.currentRide?.route && (
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                                            Route
                                            {sidePanelDriver.currentRide.route.roundTrip && (
                                                <Chip label="↺ Round Trip" size="small" color="info" sx={{ ml: 1 }} />
                                            )}
                                        </Typography>
                                        <List dense>
                                            {getRouteStops(sidePanelDriver.currentRide.route, sidePanelDriver.currentRide.route.roundTrip).map((stop, index) => (
                                                <ListItem key={index}>
                                                    <ListItemIcon>
                                                        <Chip
                                                            label={stop.label}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: stop.color,
                                                                color: 'white',
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={stop.type}
                                                        secondary={stop.address || 'N/A'}
                                                        secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Upcoming Rides Section - Collapsible, shows rides for THIS driver */}
                            <Card variant="outlined" sx={{ mb: 2 }}>
                                <ListItemButton onClick={() => setUpcomingCallsExpanded(!upcomingCallsExpanded)}>
                                    <ListItemIcon>
                                        <EventNoteIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Upcoming Calls
                                                </Typography>
                                                <Chip
                                                    label={
                                                        !sidePanelDriver.isAvailable && sidePanelDriver.currentRide
                                                            ? (sidePanelDriver.upcomingRides?.filter(r => r.rideId !== sidePanelDriver.currentRide.rideId)?.length || 0)
                                                            : (sidePanelDriver.upcomingRides?.length || 0)
                                                    }
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        }
                                    />
                                    {upcomingCallsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </ListItemButton>
                                <Collapse in={upcomingCallsExpanded} timeout="auto" unmountOnExit>
                                    <Divider />
                                    <CardContent sx={{ pt: 1 }}>
                                        {(() => {
                                            // Filter out current ride for driving drivers
                                            const filteredRides = !sidePanelDriver.isAvailable && sidePanelDriver.currentRide
                                                ? sidePanelDriver.upcomingRides?.filter(r => r.rideId !== sidePanelDriver.currentRide.rideId)
                                                : sidePanelDriver.upcomingRides;

                                            if (!filteredRides || filteredRides.length === 0) {
                                                return (
                                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                                        No upcoming rides scheduled for this driver
                                                    </Typography>
                                                );
                                            }

                                            return (
                                                <List dense>
                                                    {filteredRides.map((ride, index) => {
                                                        const stops = getStopCount(ride.route);
                                                        return (

                                                            <ListItem
                                                                key={ride.rideId || index}
                                                                divider={index < filteredRides.length - 1}
                                                                sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                                                            >
                                                                {/* Header row with customer name and time */}
                                                                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mb={0.5}>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <ScheduleIcon fontSize="small" color="action" />
                                                                        <Typography variant="body2" fontWeight="medium">
                                                                            {ride.customerName || 'Unknown'}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip
                                                                        label={formatDateTime(ride.scheduledFor)}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                                {/* Pickup line */}
                                                                <Box display="flex" alignItems="flex-start" gap={0.5} pl={1}>
                                                                    <PlaceIcon sx={{ fontSize: 14, color: 'success.main', mt: 0.3 }} />
                                                                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                                        {ride.route?.pickup || 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                                {/* Dropoff line */}
                                                                <Box display="flex" alignItems="flex-start" gap={0.5} pl={1}>
                                                                    <DropoffIcon sx={{ fontSize: 14, color: 'error.main', mt: 0.3 }} />
                                                                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                                        {ride.route?.dropOff || 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                                {!!stops && <Chip
                                                                    label={`${stops} stop${stops != 1 ? 's' : ''}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="info"
                                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                                />}
                                                            </ListItem>
                                                        )
                                                    })}
                                                </List>
                                            );
                                        })()}
                                    </CardContent>
                                </Collapse>
                            </Card>

                            {/* Location Update Info */}
                            {driverLocations[getDriverId(sidePanelDriver)] && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Last location update: {
                                            new Date(driverLocations[getDriverId(sidePanelDriver)].lastUpdate)
                                                .toLocaleTimeString()
                                        }
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Drawer>

            {/* Ride Details Panel */}
            <Drawer
                anchor="right"
                open={ridePanelOpen}
                onClose={() => {
                    setRidePanelOpen(false);
                    setSelectedRide(null);
                    setRideDirections(null);
                    setMainMapDirections(null);
                    setHighlightedDriverId(null);
                    setDriverDistanceInfo(null);
                }}
            >
                <Box sx={{ width: 400, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {selectedRide && (
                        <>
                            {/* Panel Header */}
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: getRideStatusColor(getRideStatus(selectedRide), rideStatusColors), color: getRideStatus(selectedRide) === 'inProgress' ? 'black' : 'white' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold">
                                            Ride #{selectedRide.rideId}
                                        </Typography>
                                        <Chip
                                            label={getRideStatusLabel(getRideStatus(selectedRide))}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.3)',
                                                color: 'inherit',
                                                fontWeight: 'bold',
                                                mt: 0.5
                                            }}
                                        />
                                    </Box>
                                    <IconButton
                                        onClick={() => {
                                            setRidePanelOpen(false);
                                            setSelectedRide(null);
                                            setRideDirections(null);
                                            setMainMapDirections(null);
                                            setHighlightedDriverId(null);
                                            setDriverDistanceInfo(null);
                                        }}
                                        sx={{ color: 'inherit' }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ p: 1.5, display: 'flex', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                {/* Message Driver Button */}
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<MessageIcon />}
                                    disabled={!(selectedRide.reassigned ? selectedRide.reassignedToId : selectedRide.assignedToId)}
                                    onClick={() => {
                                        const driverId = selectedRide.reassigned ? selectedRide.reassignedToId : selectedRide.assignedToId;
                                        if (driverId && openMessagingModal) {
                                            openMessagingModal(driverId);
                                        }
                                    }}
                                    sx={{ flex: 1 }}
                                >
                                    Message Driver
                                </Button>

                                {/* Find Driver Button */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<MyLocationIcon />}
                                    disabled={!(selectedRide.reassigned ? selectedRide.reassignedToId : selectedRide.assignedToId)}
                                    onClick={() => {
                                        const driverId = selectedRide.reassigned ? selectedRide.reassignedToId : selectedRide.assignedToId;
                                        if (!driverId) return;

                                        // Find driver in drivingDrivers or onlineDrivers
                                        const driverData = drivingDrivers.find(d => {
                                            const id = d.driver?.id || d.driverId || d.id;
                                            return id === driverId;
                                        }) || onlineDrivers.find(d => {
                                            const id = d.driver?.id || d.driverId || d.id;
                                            return id === driverId;
                                        });

                                        if (driverData) {
                                            const position = getDriverPosition(driverData);
                                            if (position && map) {
                                                // Pan to driver location
                                                map.panTo(position);
                                                map.setZoom(15);
                                            }
                                            // Open driver side panel
                                            const isAvailable = !drivingDrivers.some(d => {
                                                const id = d.driver?.id || d.driverId || d.id;
                                                return id === driverId;
                                            });
                                            handleMarkerClick(driverData, isAvailable);
                                            // Close ride panel
                                            setRidePanelOpen(false);
                                            setSelectedRide(null);
                                            setRideDirections(null);
                                            setMainMapDirections(null);
                                            setHighlightedDriverId(null);
                                            setDriverDistanceInfo(null);
                                        }
                                    }}
                                    sx={{ flex: 1 }}
                                >
                                    Find Driver
                                </Button>
                            </Box>

                            {/* Mini Map */}
                            <Box sx={{ height: 200, width: '100%', position: 'relative' }}>
                                <GoogleMap
                                    mapContainerStyle={{ width: '100%', height: '100%' }}
                                    center={rideLocations[selectedRide.rideId] || initialCenter.current}
                                    zoom={12}
                                    options={{
                                        ...mapOptions,
                                        zoomControl: false,
                                        mapTypeControl: false,
                                        streetViewControl: false,
                                        fullscreenControl: false
                                    }}
                                >
                                    {rideDirections && (
                                        <DirectionsRenderer
                                            directions={rideDirections}
                                            options={{
                                                suppressMarkers: false,
                                                polylineOptions: {
                                                    strokeColor: '#1976d2',
                                                    strokeWeight: 4
                                                }
                                            }}
                                        />
                                    )}
                                    {!rideDirections && rideLocations[selectedRide.rideId] && (
                                        <Marker
                                            position={rideLocations[selectedRide.rideId]}
                                            icon={getRideIcon(getRideStatus(selectedRide), markerColors)}
                                        />
                                    )}
                                    {/* Show assigned driver's location on mini map */}
                                    {(() => {
                                        const assignedDriverId = selectedRide.reassigned ? selectedRide.reassignedToId : selectedRide.assignedToId;
                                        if (!assignedDriverId) return null;
                                        const driverData = drivingDrivers.find(d => {
                                            const id = d.driver?.id || d.driverId || d.id;
                                            return id === assignedDriverId;
                                        }) || onlineDrivers.find(d => {
                                            const id = d.driver?.id || d.driverId || d.id;
                                            return id === assignedDriverId;
                                        });
                                        const driverPos = driverData ? getDriverPosition(driverData) : null;
                                        if (!driverPos) return null;
                                        return (
                                            <Marker
                                                position={driverPos}
                                                icon={{
                                                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
                                                            <circle cx="12" cy="12" r="10" fill="#1976d2" stroke="white" stroke-width="2"/>
                                                            <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold">🚗</text>
                                                        </svg>
                                                    `),
                                                    scaledSize: new window.google.maps.Size(32, 32),
                                                    anchor: new window.google.maps.Point(16, 16)
                                                }}
                                                title="Driver Location"
                                            />
                                        );
                                    })()}
                                </GoogleMap>
                            </Box>

                            {/* Ride Details Content */}
                            <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                                {/* Client Info */}
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Client Information
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold">
                                            {selectedRide.customerName || 'Unknown Client'}
                                        </Typography>
                                        {selectedRide.customerPhoneNumber && (
                                            <Typography variant="body2" color="text.secondary">
                                                📞 {selectedRide.customerPhoneNumber}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Schedule Info */}
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Schedule
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <ScheduleIcon color="primary" />
                                            <Typography variant="body1" fontWeight="medium">
                                                {selectedRide.scheduledFor
                                                    ? formatDateTime(selectedRide.scheduledFor)
                                                    : 'ASAP'
                                                }
                                            </Typography>
                                        </Box>
                                        {selectedRide.returnTime && (
                                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Return: {formatTime(selectedRide.returnTime)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Route Info */}
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Route Details
                                        </Typography>

                                        {/* Pickup */}
                                        <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                                            <PlaceIcon sx={{ color: 'success.main', mt: 0.3 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Pickup</Typography>
                                                <Typography variant="body2">{selectedRide.route?.pickup || 'N/A'}</Typography>
                                            </Box>
                                        </Box>

                                        {/* Stops */}
                                        {(() => {
                                            const stopFields = ['stop1', 'stop2', 'stop3', 'stop4', 'stop5', 'stop6', 'stop7', 'stop8', 'stop9', 'stop10'];
                                            const stops = stopFields.filter(f => selectedRide.route?.[f]);
                                            return stops.map((stopField, idx) => (
                                                <Box key={stopField} display="flex" alignItems="flex-start" gap={1} mb={1} pl={1}>
                                                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'warning.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, mt: 0.3 }}>
                                                        {idx + 1}
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Stop {idx + 1}</Typography>
                                                        <Typography variant="body2">{selectedRide.route[stopField]}</Typography>
                                                    </Box>
                                                </Box>
                                            ));
                                        })()}

                                        {/* Dropoff */}
                                        <Box display="flex" alignItems="flex-start" gap={1}>
                                            <DropoffIcon sx={{ color: 'error.main', mt: 0.3 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Drop-off</Typography>
                                                <Typography variant="body2">{selectedRide.route?.dropOff || 'N/A'}</Typography>
                                            </Box>
                                        </Box>

                                        {selectedRide.route?.roundTrip && (
                                            <Chip
                                                label="Round Trip"
                                                size="small"
                                                color="info"
                                                sx={{ mt: 1 }}
                                            />
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Driver Assignment */}
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Driver Assignment
                                        </Typography>
                                        {(selectedRide.reassigned ? selectedRide.reassignedTo : selectedRide.assignedTo) ? (
                                            <>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <PersonIcon color="primary" />
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {selectedRide.reassigned ? selectedRide.reassignedTo?.name : selectedRide.assignedTo?.name}
                                                    </Typography>
                                                </Box>

                                                {/* Driver distance to pickup - show for assigned rides not yet picked up */}
                                                {!selectedRide.pickupTime && !selectedRide.arrivedAtPickupTime && driverDistanceInfo && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                                                        <Typography variant="caption" color="info.dark" fontWeight="medium">
                                                            🚗 Driver is {driverDistanceInfo.distance} away ({driverDistanceInfo.duration})
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Driver waiting at pickup - show when arrivedAtPickupTime but no pickupTime */}
                                                {selectedRide.arrivedAtPickupTime && !selectedRide.pickupTime && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.lighter', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                                                        <Typography variant="caption" color="warning.dark" fontWeight="medium">
                                                            ⏳ Driver waiting at pickup for {(() => {
                                                                const arrivedDate = new Date(selectedRide.arrivedAtPickupTime);
                                                                const now = new Date();
                                                                const diffMs = now - arrivedDate;
                                                                const diffMins = Math.floor(diffMs / 60000);
                                                                if (diffMins < 1) return 'less than a minute';
                                                                if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''}`;
                                                                const hours = Math.floor(diffMins / 60);
                                                                const mins = diffMins % 60;
                                                                return `${hours}h ${mins}m`;
                                                            })()}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Elapsed time since pickup - show for rides in progress */}
                                                {selectedRide.pickupTime && !selectedRide.dropOffTime && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'success.lighter', borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
                                                        <Typography variant="caption" color="success.dark" fontWeight="medium">
                                                            ✅ Picked up {(() => {
                                                                const pickupDate = new Date(selectedRide.pickupTime);
                                                                const now = new Date();
                                                                const diffMs = now - pickupDate;
                                                                const diffMins = Math.floor(diffMs / 60000);
                                                                if (diffMins < 1) return 'just now';
                                                                if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
                                                                const hours = Math.floor(diffMins / 60);
                                                                const mins = diffMins % 60;
                                                                return `${hours}h ${mins}m ago`;
                                                            })()}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="body2" color="warning.main">
                                                ⚠️ Unassigned
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Additional Info */}
                                {(selectedRide.notes || selectedRide.passengers || selectedRide.cost) && (
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Additional Info
                                            </Typography>
                                            {selectedRide.passengers && (
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    👥 {selectedRide.passengers} passenger{selectedRide.passengers !== 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                            {selectedRide.cost > 0 && (
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    💰 ${selectedRide.cost.toFixed(2)}
                                                </Typography>
                                            )}
                                            {selectedRide.notes && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    📝 {selectedRide.notes}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Timestamps */}
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Created: {selectedRide.callTime ? formatDateTime(selectedRide.callTime) : 'N/A'}
                                    </Typography>
                                    {selectedRide.arrivedAtPickupTime && (
                                        <Typography variant="caption" color="info.main" display="block">
                                            Arrived at pickup: {formatTime(selectedRide.arrivedAtPickupTime)}
                                        </Typography>
                                    )}
                                    {selectedRide.pickupTime && (
                                        <Typography variant="caption" color="success.main" display="block">
                                            Picked up: {formatTime(selectedRide.pickupTime)}
                                        </Typography>
                                    )}
                                    {selectedRide.dropOffTime && (
                                        <Typography variant="caption" color="primary" display="block">
                                            Dropped off: {formatTime(selectedRide.dropOffTime)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Drawer>
        </Box>
    );
};

export default DriverTracking;
